import "./App.css";
import { useEffect, useRef, useState } from "react";
import { Row, RowState } from "./Row";
import dictionary from "./dictionary.json";
import naughty from "./naughty.json";
import { Clue, clue, describeClue, violation } from "./clue";
import { Keyboard } from "./Keyboard";
import targetList from "./targets.json";
import {
  describeSeed,
  dictionarySet,
  Difficulty,
  pick,
  resetRng,
  seed,
  speak,
  urlParam,
} from "./util";
import { decode, encode } from "./base64";
import { todaySeed } from "./App";

export var endStateMessage = "Beginning value";
export var targetString = "target word";

export var endStatePage = "hidden";

enum GameState {
  Playing,
  Won,
  Lost,
}

enum EndgameModule {
    Open,
    Closed,
}

enum IntroState {
    Open,
    Closed,
}

enum TodayCheckState {
    Open,
    Closed,
}


enum GuessType
{
    Unset,
    Valid,
    Invalid,
}

interface GameProps {
  maxGuesses: number;
  hidden: boolean;
  difficulty: Difficulty;
  colorBlind: boolean;
  //keyboardLayout: string;
}

const targets = targetList;
export const minLength = 4;
export const maxLength = 11;

function randomTarget(wordLength: number): string {
  const eligible = targets.filter((word) => word.length === wordLength);
  let candidate: string;
  do {
    candidate = pick(eligible);
  } while (/\*/.test(candidate));
  return candidate;
}

function getChallengeUrl(target: string): string {
  return (
    window.location.origin +
    window.location.pathname +
    "?challenge=" +
    encode(target)
  );
}

let initChallenge = "";
let challengeError = false;
try {
  initChallenge = decode(urlParam("challenge") ?? "").toLowerCase();
} catch (e) {
  console.warn(e);
  challengeError = true;
}
if (initChallenge && !dictionarySet.has(initChallenge)) {
  initChallenge = "";
  challengeError = true;
}

function parseUrlLength(): number {
  const lengthParam = urlParam("length");
  if (!lengthParam) return 5;
  const length = Number(lengthParam);
  return length >= minLength && length <= maxLength ? length : 5;
}

function parseUrlGameNumber(): number {
  const gameParam = urlParam("game");
  if (!gameParam) return 1;
  const gameNumber = Number(gameParam);
  return gameNumber >= 1 && gameNumber <= 1000 ? gameNumber : 1;
}

function Game(props: GameProps) {
  const [gameState, setGameState] = useState(GameState.Playing);
  const [endgameModule, setEndgameModule] = useState(EndgameModule.Closed);
  const [introState, setIntroState] = useState(IntroState.Open);
  const [todayCheckState, setTodayCheckState] = useState(TodayCheckState.Open);
  const [guessType, setGuessType] = useState(GuessType.Unset);
  const [guesses, setGuesses] = useState<string[]>([]);
  const [currentGuess, setCurrentGuess] = useState<string>("");
  const [challenge, setChallenge] = useState<string>(initChallenge);
  const [wordLength, setWordLength] = useState(challenge ? challenge.length : parseUrlLength());


  const [gameNumber, setGameNumber] = useState(parseUrlGameNumber());
    const [target, setTarget] = useState(() => {
    resetRng();
    // Skip RNG ahead to the parsed initial game number:
    for (let i = 1; i < gameNumber; i++) randomTarget(wordLength);
    return challenge || randomTarget(wordLength);
  });

  const [hint, setHint] = useState<string>(
    challengeError
      ? `Invalid challenge string, playing random game.`
      : `Make your first guess!`
  );

    endStateMessage = hint;
    targetString = target;

    const wordURLsearch = 'https://www.folger.edu/search/?q='+targetString+'&area=works';
    const wordURLsearchCurrent = 'https://www.folger.edu/search/?q='+currentGuess+'&area=works';



    let FSSLinkMessage = function (gameover: GameState) {

        if (naughty.includes(currentGuess) && hint.length) {
            return (<p>Read how Shakespeare used "<strong>{currentGuess}</strong>" at <a href={wordURLsearchCurrent} target="_blank" rel="noreferrer">The Folger Shakespeare</a></p>);
        }

        if (gameover !== GameState.Playing ) {
            return (<p>Read how Shakespeare used "<strong>{targetString}</strong>" at <a href={wordURLsearch} target="_blank" rel="noreferrer">The Folger Shakespeare</a></p>);
        }
        else {
            return (<p>All Prattle words are from the texts of <a href="https://www.folger.edu/explore/shakespeares-works/">The Folger Shakespeare</a></p>);
        }
    };

  const currentSeedParams = () =>
    `?seed=${seed}&length=${wordLength}&game=${gameNumber}`;
  useEffect(() => {
      if (seed) {
          window.history.replaceState(
              {},
              document.title,
              window.location.pathname + currentSeedParams()
          );
      }
  }, [wordLength, gameNumber]);
  const tableRef = useRef<HTMLTableElement>(null);

    const startNextGame = () => {
    if (challenge) {
      // Clear the URL parameters:
      //window.history.replaceState({}, document.title, window.location.pathname);
    }
    setChallenge("");
    const newWordLength =
      wordLength >= minLength && wordLength <= maxLength ? wordLength : 5;
    setWordLength(newWordLength);
    setTarget(randomTarget(newWordLength));
        setHint(" ");
    setGuesses([]);
    setCurrentGuess("");
    setGameState(GameState.Playing);
    setEndgameModule(EndgameModule.Closed);
    setGuessType(GuessType.Unset);
    setGameNumber((x) => x + 1);
    FSSLinkMessage(gameState);
    };

    async function share(copiedHint: string, text?: string) {
        let gametype = document.getElementById("gameType")!.innerHTML;
    const url = seed
      ? window.location.origin + window.location.pathname + currentSeedParams()
      : getChallengeUrl(target);
        const body = (text ? "PRATTLE: " + gametype + "\n\n" + text + "\n\n"  : "")+ url;
    if (
      /android|iphone|ipad|ipod|webos/i.test(navigator.userAgent) &&
      !/firefox/i.test(navigator.userAgent)
    ) {
      try {
        await navigator.share({ text: body });
        return;
      } catch (e) {
        console.warn("navigator.share failed:", e);
      }
    }
    try {
      await navigator.clipboard.writeText(body);
      setHint(copiedHint);
      return;
    } catch (e) {
      console.warn("navigator.clipboard.writeText failed:", e);
    }
    setHint(url);
  }

    const onKey = (key: string) => {
      if (gameState !== GameState.Playing) {
      if (key === "Enter") {
        startNextGame();
      }
      return;
    }
    if (guesses.length === props.maxGuesses) return;
        if (/^[a-z]$/i.test(key)) {
            setCurrentGuess((guess) =>
                (guess + key.toLowerCase()).slice(0, wordLength)
            );
            tableRef.current?.focus();
            setHint("");
        } else if (key === "Backspace") {
            setCurrentGuess((guess) => guess.slice(0, -1));
            setHint("");
        } else if (key === "Enter") {
            if (currentGuess.length !== wordLength) {
                setHint("Too short");
                setGuessType(GuessType.Invalid);
                return;
            }
            if (!dictionary.includes(currentGuess)) {
                if (naughty.includes(currentGuess)) {
                    setHint("Shakespeare did use this word, but we decided not to. Please guess a new word to keep playing.");
                    setGuessType(GuessType.Invalid);
                }
                else {
                    setHint("Not a word from Shakespeare");
                    setGuessType(GuessType.Invalid);
                }
                return;
            }
            for (const g of guesses) {
                const c = clue(g, target);
                const feedback = violation(props.difficulty, c, currentGuess);
                if (feedback) {
                    setHint(feedback);
                    return;
                }
            }
            setGuesses((guesses) => guesses.concat([currentGuess]));
            setCurrentGuess((guess) => "");



            const gameOver = (verbed: string) =>
                `You ${verbed}! The answer was ${target.toUpperCase()}`;

            if (currentGuess === target) {
                setHint(gameOver("won"));
                setGameState(GameState.Won);
                setEndgameModule(EndgameModule.Open);
            } else if (guesses.length + 1 === props.maxGuesses) {
                setHint(gameOver("lost"));
                setGameState(GameState.Lost);
                setEndgameModule(EndgameModule.Open);
            } else {
                setHint("");
                speak(describeClue(clue(currentGuess, target)));
            }
        }
    };



    useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (!e.ctrlKey && !e.metaKey) {
        onKey(e.key);
      }
      if (e.key === "Backspace") {
        e.preventDefault();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [currentGuess, gameState]);

  let letterInfo = new Map<string, Clue>();
  const tableRows = Array(props.maxGuesses)
    .fill(undefined)
    .map((_, i) => {
      const guess = [...guesses, currentGuess][i] ?? "";
      const cluedLetters = clue(guess, target);
      const lockedIn = i < guesses.length;
      if (lockedIn) {
        for (const { clue, letter } of cluedLetters) {
          if (clue === undefined) break;
          const old = letterInfo.get(letter);
          if (old === undefined || clue > old) {
            letterInfo.set(letter, clue);
          }
        }
      }
        return (
        <Row
          key={i}
          wordLength={wordLength}
          rowState={
            lockedIn
              ? RowState.LockedIn
              : i === guesses.length
              ? RowState.Editing
              : RowState.Pending
          }
          cluedLetters={cluedLetters}
        />
      );
    });

    let todaygame_checked: boolean = false;

  return (
      <div className="Game" style={{ display: props.hidden ? "none" : "flex" }}>


          {todayCheckState !== TodayCheckState.Closed && (urlParam("random") == null && (seed !== parseInt(todaySeed) && (
              <div id="todaygame-check" className="popup">
                  <h1> Welcome to PRATTLE!</h1>
                  <p>You're about to play a {urlParam("challenge") !== null && (<strong><br />challenge game</strong>) || <span> game from <strong><br />{describeSeed(seed)}</strong></span>}</p>
                  <button
                      className="button_default"
                      onClick={() => {
                          setTodayCheckState(TodayCheckState.Closed);
                      }}
                  >Continue
                    </button>
                      { " or " }
                  <button
                      className="button_default"
                  ><a href="/">Play Today's</a></button>
              </div>
          )))
          }

          {introState !== IntroState.Closed && (
              <div id="introduction" className="popup">
                  <div className="close_container">
                  <button id="close_endstate"
                      onClick={() => { setIntroState(IntroState.Closed); }}
                  >
                          X
              </button>
                    </div>

                  <span><strong>How to</strong></span>
                  <Row
                      rowState={RowState.LockedIn}
                      wordLength={4}
                      cluedLetters={[
                          { clue: Clue.Absent, letter: "p" },
                          { clue: Clue.Elsewhere, letter: "l" },
                          { clue: Clue.Absent, letter: "a" },
                          { clue: Clue.Absent, letter: "y" },
                      ]}
                  />
                  <br />
                  <span>You have six tries to guess the correct</span>
                  <Row
                      rowState={RowState.LockedIn}
                      wordLength={4}
                      cluedLetters={[
                          { clue: Clue.Absent, letter: "w" },
                          { clue: Clue.Correct, letter: "o" },
                          { clue: Clue.Absent, letter: "r" },
                          { clue: Clue.Absent, letter: "d" },
                      ]}
                  />
                  <br />
                  <span>Incorrect guesses appear in</span>
                  <Row
                      rowState={RowState.LockedIn}
                      wordLength={4}
                      cluedLetters={[
                          { clue: Clue.Absent, letter: "g" },
                          { clue: Clue.Absent, letter: "r" },
                          { clue: Clue.Absent, letter: "a" },
                          { clue: Clue.Absent, letter: "y" },
                      ]}
                  />
                  <br />
                  <span>A correct letter, but in the wrong spot, will appear</span>
                  <Row
                      rowState={RowState.LockedIn}
                      wordLength={4}
                      cluedLetters={[
                          { clue: Clue.Absent, letter: "p" },
                          { clue: Clue.Absent, letter: "a" },
                          { clue: Clue.Elsewhere, letter: "l" },
                          { clue: Clue.Absent, letter: "e" },
                      ]}
                  />
                  <br />
                  <span>A correct letter, in the correct spot, will appear</span>
                  <Row
                      rowState={RowState.LockedIn}
                      wordLength={4}
                      cluedLetters={[
                          { clue: Clue.Absent, letter: "b" },
                          { clue: Clue.Correct, letter: "o" },
                          { clue: Clue.Elsewhere, letter: "l" },
                          { clue: Clue.Absent, letter: "d" },
                      ]}
                  />
                  <br />
                  <span>You get six guesses. So feel free to</span>
                  <Row
                      rowState={RowState.LockedIn}
                      wordLength={4}
                      cluedLetters={[
                          { clue: Clue.Correct, letter: "f" },
                          { clue: Clue.Correct, letter: "o" },
                          { clue: Clue.Correct, letter: "o" },
                          { clue: Clue.Correct, letter: "l" },
                      ]}
                  />
                  <span>around until you get it right!</span>
                  <p>
                      "I will be a <strong>fool</strong> in question, hoping to be the wiser by your
              answer."
              <br />
                      <a href="https://www.folger.edu/explore/shakespeares-works/alls-well-that-ends-well/read/#line-2.2.38" target="_blank" rel="noreferrer"><i>All's Well That End's Well</i>, 2.2.38–40.</a>
                  </p>

              </div>
          )
          }
          <div className="Game-seed-info">
              <span id="gameType">{challenge
                  ? "playing a challenge game"
                  : seed
                      ? `${describeSeed(seed)} — length ${wordLength}, game ${gameNumber}`
                      : "playing a random game"}
              </span>
              { " " }
              {urlParam("challenge") == null && (urlParam("random") == null && (seed !== parseInt(todaySeed) && (
                  <button
                      className="button_default"

                      onClick={() => { startNextGame(); }}
                  ><a href="/">Play Today's</a></button>

                  )))}
          </div>
          <div
              className="game-options"
          >Switch to <a
                  href={seed ? "?random" : "?seed=" + todaySeed}>
                      {seed ? "Random" : "Today's"} Game
              </a>
              { " | "}
        <label htmlFor="wordLength">Letters:</label>
              <input
           className="letter-length"
          type="range"
          min={minLength}
          max={maxLength}
          id="wordLength"
          disabled={
            gameState === GameState.Playing &&
            (guesses.length > 0 || currentGuess !== "" || challenge !== "")
          }
          value={wordLength}
          onChange={(e) => {
            const length = Number(e.target.value);
            resetRng();
            setGameNumber(1);
            setGameState(GameState.Playing);
            setGuesses([]);
            setCurrentGuess("");
            setTarget(randomTarget(length));
            setWordLength(length);
            setHint(`${length} letters`);
          }}
              ></input>              {" | "}

              <button
          className="button_default"
          style={{ flex: "0 0 auto" }}
          disabled={gameState !== GameState.Playing || guesses.length === 0}
          onClick={() => {
            setHint(
              `The answer was ${target.toUpperCase()}.`
            );
              setGameState(GameState.Lost);
              setEndgameModule(EndgameModule.Open);
            (document.activeElement as HTMLElement)?.blur();
          }}
        >
          Give up
        </button>
          </div>

          <div className="board-container">
      <table
        className="Game-rows"
        tabIndex={0}
        aria-label="Table of guesses"
        ref={tableRef}
              >

        <tbody>{tableRows}</tbody>
              </table>



              {hint.length ? (

              <div className="hint">
                  <p
                      role="alert"
                      style={{
                          userSelect: /https?:/.test(hint) ? "text" : "none",
                          whiteSpace: "pre-wrap",
                      }}
                  >{hint || `\u00a0`}{" "}{guessType === GuessType.Invalid && (
                      <button
                          className="button_default"

                          onClick={() => {
                              setCurrentGuess("");
                              setHint("");
                              setGuessType(GuessType.Unset);
                          }}
                      >
                          Clear Guess
                      </button>
                  )}
                  </p>
              </div>) : (<div className="no-hint"><p>&nbsp;</p></div>)}
          { FSSLinkMessage(gameState) }
          {gameState !== GameState.Playing && (
              <p><strong>Press ENTER to play a new game</strong></p>
          )}
              {gameState === GameState.Playing && (
              <p>{}</p>
          )}
          </div>
          <Keyboard letterInfo={letterInfo} onKey={onKey} />
          <p>
              {gameState !== GameState.Playing && (
                  <button
                      className="button_default"

                      onClick={() => { startNextGame(); }}
                  >
                      New Game
                  </button>
              )}
              {" "}
              <button
                  className="button_default"
          onClick={() => {
            share("Link copied to clipboard!");
          }}
        >
                  Share a link to this game
        </button>{" "}
        {gameState !== GameState.Playing && (
                  <button
                      className="button_default"
            onClick={() => {
              const emoji = props.colorBlind
                ? ["⬜️", "🟦", "🟧"]
                  : ["⬜️", "🟦", "🟧"];
              share(
                "Result copied to clipboard!",
                guesses
                  .map((guess) =>
                    clue(guess, target)
                      .map((c) => emoji[c.clue ?? 0])
                      .join("")
                  )
                  .join("\n")
              );
            }}
          >
                      Share emoji results
                  </button>
        )}
          </p>
          {endgameModule !== EndgameModule.Closed && (
              <div id="endstate" className="popup">

                  <div className="close_container">


                  <button id="close_endstate"
                      onClick={() => {setEndgameModule(EndgameModule.Closed); }}
                  >
                          X
              </button>
                  </div>

                  <h1>{endStateMessage}</h1>
                  {FSSLinkMessage(gameState)}
                  <button
                      className="button_default"

                      onClick={() => { startNextGame(); }}
                  >
                      New Game
                  </button>
                  {" "}
                  <button
                      className="button_default"

                      onClick={() => {
                          share("Link copied to clipboard!");
                      }}
                  >
                      Share a link to this game
        </button>{" "}
                  <button
                      className="button_default"

                      onClick={() => {
                          const emoji = props.colorBlind
                              ? ["⬜️", "🟦", "🟧"]
                              : ["⬜️", "🟦", "🟧"];
                          share(
                              "Result copied to clipboard!",
                              guesses
                                  .map((guess) =>
                                      clue(guess, target)
                                          .map((c) => emoji[c.clue ?? 0])
                                          .join("")
                                  )
                                  .join("\n")
                          );
                      }}
                  >
                      Share emoji results
                  </button>
              </div>
          )}
      </div>
    );
}
export default Game;
